/**
 * Read-only views over a KBStore: the rendered tree, paths, stats and search.
 *
 * Split from store.ts so it is obvious at a glance which functions mutate and
 * which are safe to call from a render path.
 */

import {
  type KBStore, type AnyNode, type CategoryNode, type SectionNode,
  type ArticleNode, type NodeKind, type PublishState,
  articleState, localesByState,
} from "./types"

// ── Queries ──────────────────────────────────────────────────────────────────

export function childSections(store: KBStore, opts: { categoryId?: string; parentSectionId?: string | null }): SectionNode[] {
  return Object.values(store.sections)
    .filter(s => {
      if (opts.categoryId && s.categoryId !== opts.categoryId) return false
      if (opts.parentSectionId !== undefined && (s.parentSectionId ?? null) !== (opts.parentSectionId ?? null)) return false
      return true
    })
    .sort((a, b) => a.position - b.position)
}

export function articlesIn(store: KBStore, sectionId: string): ArticleNode[] {
  return Object.values(store.articles)
    .filter(a => a.sectionId === sectionId)
    .sort((a, b) => a.position - b.position)
}

export function allCategories(store: KBStore): CategoryNode[] {
  return Object.values(store.categories).sort((a, b) => a.position - b.position)
}

/** Human-readable breadcrumb, used in agent tool output so the LLM keeps its bearings. */
export function pathOf(store: KBStore, id: string, locale: string): string {
  const parts: string[] = []
  const t = (n: AnyNode) => n.translations[locale]?.title ?? n.translations[n.sourceLocale]?.title ?? "(untitled)"
  const a = store.articles[id]
  if (a) {
    parts.push(t(a))
    let sid: string | null = a.sectionId
    while (sid && store.sections[sid]) {
      parts.unshift(t(store.sections[sid]))
      sid = store.sections[sid].parentSectionId
    }
    const cat = store.categories[store.sections[a.sectionId]?.categoryId ?? ""]
    if (cat) parts.unshift(t(cat))
    return parts.join(" / ")
  }
  const s = store.sections[id]
  if (s) {
    let sid: string | null = id
    while (sid && store.sections[sid]) {
      parts.unshift(t(store.sections[sid]))
      sid = store.sections[sid].parentSectionId
    }
    const cat = store.categories[s.categoryId]
    if (cat) parts.unshift(t(cat))
    return parts.join(" / ")
  }
  const c = store.categories[id]
  return c ? t(c) : "(unknown)"
}

export function stats(store: KBStore, locale?: string) {
  const articles = Object.values(store.articles)
  // Publish state is per-locale, so "drafts" is ambiguous without one. With a
  // locale, count that locale; without, count articles with any draft locale.
  const isDraft = (a: typeof articles[number]) =>
    locale ? articleState(a, locale) === "draft" : localesByState(a).draft.length > 0
  return {
    categories: Object.keys(store.categories).length,
    sections: Object.keys(store.sections).length,
    articles: articles.length,
    drafts: articles.filter(isDraft).length,
    published: articles.filter(a => !isDraft(a)).length,
    locales: store.locales.length,
  }
}

export interface SearchHit {
  node: AnyNode
  locale: string
  snippet: string
  /** Relevance score; higher is better. */
  score: number
  /** Which query terms were found. */
  matchedTerms: string[]
  /** Where the match landed, for explaining results. */
  matchedIn: Array<"title" | "description" | "body" | "label">
}

export interface SearchOptions {
  limit?: number
  /** Restrict to one kind of node. */
  kind?: NodeKind
  /** Restrict to one locale instead of searching all of them. */
  locale?: string
  /** Only articles in this state. */
  state?: PublishState
  /** Only articles carrying ALL of these labels. */
  labels?: string[]
  /** Only nodes inside this category or section subtree. */
  withinId?: string
  /** All terms must match, rather than any. */
  requireAll?: boolean
}

const STOPWORDS = new Set([
  "the", "a", "an", "and", "or", "of", "to", "in", "for", "on", "is", "are", "be",
  "with", "how", "what", "do", "does", "i", "my", "it", "this", "that", "at", "by",
])

function tokenize(s: string): string[] {
  return (s.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []).filter(Boolean)
}

/** Descendant ids of a category or section, for scoping a search. */
function subtreeIds(store: KBStore, rootId: string): Set<string> {
  const ids = new Set<string>([rootId])
  const addSection = (sectionId: string) => {
    ids.add(sectionId)
    for (const a of Object.values(store.articles)) if (a.sectionId === sectionId) ids.add(a.id)
    for (const s of Object.values(store.sections)) if (s.parentSectionId === sectionId) addSection(s.id)
  }
  if (store.categories[rootId]) {
    for (const s of Object.values(store.sections)) if (s.categoryId === rootId) addSection(s.id)
  } else if (store.sections[rootId]) {
    addSection(rootId)
  }
  return ids
}

/**
 * Keyword search across all content.
 *
 * Term-based rather than substring: the query is tokenized, stopwords dropped,
 * and each node scored by where its terms hit. Title matches outrank body
 * matches, exact-phrase matches outrank scattered terms, and prefix matches
 * count partially so "hous" still finds "housing". No dependencies — this is a
 * small in-memory index built per call, which is fine at prototype scale.
 */
export function searchNodes(store: KBStore, query: string, opts: SearchOptions | number = {}): SearchHit[] {
  // Back-compat: earlier callers passed a bare `limit`.
  const o: SearchOptions = typeof opts === "number" ? { limit: opts } : opts
  const limit = o.limit ?? 25

  const phrase = query.toLowerCase().trim()
  if (!phrase) return []
  const allTerms = tokenize(phrase)
  const terms = allTerms.filter(t => !STOPWORDS.has(t))
  const effective = terms.length ? terms : allTerms
  if (!effective.length) return []

  const scope = o.withinId ? subtreeIds(store, o.withinId) : null
  const hits: SearchHit[] = []

  const consider = (node: AnyNode) => {
    if (o.kind && node.kind !== o.kind) return
    if (scope && !scope.has(node.id)) return
    if (node.kind === "article") {
      const a = node as ArticleNode
      // Publish state is per-locale, so match if ANY locale is in that state.
      if (o.state && !Object.keys(a.translations).some(l => articleState(a, l) === o.state)) return
      if (o.labels?.length) {
        const have = new Set(a.labels.map(l => l.toLowerCase()))
        if (!o.labels.every(l => have.has(l.toLowerCase()))) return
      }
    } else if (o.state || o.labels?.length) {
      // State/label filters only apply to articles.
      return
    }

    let best: SearchHit | null = null

    for (const [locale, tr] of Object.entries(node.translations)) {
      if (o.locale && locale !== o.locale) continue
      // The node-level state filter above admits an article if ANY locale is
      // in the requested state. Without this, the best-scoring locale could be
      // a different one, and the hit would report a state contradicting the
      // filter that selected it.
      if (o.state && node.kind === "article" && articleState(node as ArticleNode, locale) !== o.state) continue

      const titleText = (tr.title || "").toLowerCase()
      const descText = (tr.description || "").toLowerCase()
      const bodyText = stripHtml(tr.body || "").toLowerCase()
      const labelText = node.kind === "article"
        ? (node as ArticleNode).labels.join(" ").toLowerCase()
        : ""

      const titleTokens = new Set(tokenize(titleText))
      const descTokens = new Set(tokenize(descText))
      const bodyTokens = new Set(tokenize(bodyText))
      const labelTokens = new Set(tokenize(labelText))

      let score = 0
      const matched = new Set<string>()
      const where = new Set<"title" | "description" | "body" | "label">()

      for (const term of effective) {
        // Exact token matches, weighted by field.
        if (titleTokens.has(term)) { score += 10; matched.add(term); where.add("title") }
        else if (labelTokens.has(term)) { score += 6; matched.add(term); where.add("label") }
        else if (descTokens.has(term)) { score += 4; matched.add(term); where.add("description") }
        else if (bodyTokens.has(term)) { score += 2; matched.add(term); where.add("body") }
        // Prefix fallback so partial words still find content. Requires 3+
        // chars — shorter prefixes match almost everything and turn a query
        // into noise (e.g. "a" prefixing "at", "all", "and").
        else if (term.length >= 3) {
          if ([...titleTokens].some(t => t.startsWith(term))) { score += 5; matched.add(term); where.add("title") }
          else if ([...bodyTokens].some(t => t.startsWith(term))) { score += 1; matched.add(term); where.add("body") }
        }
      }

      if (!matched.size) continue
      if (o.requireAll && matched.size < effective.length) continue

      // Whole-phrase hits are strong signals.
      if (effective.length > 1) {
        if (titleText.includes(phrase)) score += 15
        else if (bodyText.includes(phrase)) score += 6
      }
      // Prefer nodes matching more of the query.
      score += matched.size * 2

      // Build a snippet around the first body occurrence, else use the title.
      const anchorTerm = effective.find(t => bodyText.includes(t))
      let snippet: string
      if (bodyText.includes(phrase)) {
        const at = bodyText.indexOf(phrase)
        snippet = stripHtml(tr.body).slice(Math.max(0, at - 60), at + 160).trim()
      } else if (anchorTerm) {
        const at = bodyText.indexOf(anchorTerm)
        snippet = stripHtml(tr.body).slice(Math.max(0, at - 60), at + 160).trim()
      } else {
        snippet = (tr.description || stripHtml(tr.body)).slice(0, 160).trim()
      }

      const hit: SearchHit = {
        node, locale, snippet, score,
        matchedTerms: [...matched],
        matchedIn: [...where],
      }
      if (!best || hit.score > best.score) best = hit
    }

    if (best) hits.push(best)
  }

  Object.values(store.categories).forEach(consider)
  Object.values(store.sections).forEach(consider)
  Object.values(store.articles).forEach(consider)

  return hits.sort((a, b) => b.score - a.score).slice(0, limit)
}

export function stripHtml(html: string): string {
  return (html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
}
